import { useEffect, useState, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useHeaderContext } from "../../../context/headerContext";
import { useDrawerContext } from "../../../context/drawerContext";
import { useUserContext } from "../../../context/userContext";
import { z } from "zod";
import TextInput from "../../../components/TextInput";
import TextArea from "../../../components/TextArea";
import Button from "../../../components/Button";
import CloseIcon from "/public/images/icons/close.svg?react";
import fetchWithRefresh from "../../../utils/fetchWithRefresh";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { LogErrors } from "../../../utils/LogErrors";

export default function Activity() {
  const { setTitle, setBackButton, setBackButtonFunc } = useHeaderContext();
  const { setOpen, setDrawerContent } = useDrawerContext();
  const { state } = useLocation();
  const { user, logout, updateTokens } = useUserContext();
  const formRef = useRef(null);
  const navigate = useNavigate();
  const { activity, selectedParticipant } = state;
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const [formDetails] = useState({
    activity_location: activity.orgPartnerFriendlyName || activity.location,
    activity_name: activity.activityName,
    activity_notes: activity.notes,
  });

  useEffect(() => {
    setTitle(activity.activityName);
    setBackButton(true);
  }, [activity.activityName]);

  useEffect(() => {
    const handleBack = () => {
      if (unsavedChanges) {
        unsavedChangesDrawer();
      } else {
        navigate("/log", { state: { selectedParticipant } });
      }
    };

    setBackButtonFunc(handleBack);
  }, [unsavedChanges, navigate, selectedParticipant]);

  const inputChanged = (name, value) => {
    if (formDetails[name] !== value) {
      setUnsavedChanges(true);
    } else {
      setUnsavedChanges(false);
    }
  };

  const openDrawer = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const handleDrawerSubmit = () => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
    setOpen(false);
    navigate("/log", { state: { selectedParticipant } });
  };

  useEffect(() => {
    if (user.subscription.Name === 'Bronze') {
      setOpen(true);
      setDrawerContent(
        <div className="w-full">
          <div className="flex items-center justify-between w-full">
            <br />
            <h2 className="leading-5 tracking-widest uppercase text-[12px]">
              UPGRADE
            </h2>
            <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
          </div>
          <div className="mt-6 flex flex-col gap-2 justify-stretch">
            <h3 className="text-[24px]">Upgrade subscription</h3>
            <p className="text-sportsDarkGrey mb-[26px]">
              Based on your <b>bronze</b> subscription level, you can only view activities. To manually update your activities, please upgrade your subscription.
            </p>
            <Button className="w-full" variant="primary" href="/upgrade">
              Upgrade My Subscription
            </Button>
          </div>
        </div>
      );

      setBackButtonFunc(openDrawer);
    }
  }, [user]);

  const unsavedChangesDrawer = () => {
    setOpen(true);
    setDrawerContent(
      <div className="w-full">
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">
            UNSAVED CHANGES
          </h2>
          <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
        </div>
        <div className="mt-6 flex flex-col gap-2 justify-stretch">
          <h3 className="text-[24px]">Changes have been made</h3>
          <p className="text-sportsDarkGrey mb-[26px]">
            Some activity details have been changed and are currently unsaved.
            Do you want to continue without saving?
          </p>
          <div className="flex flex-col gap-6">
            <Button className="w-full" onClick={handleDrawerSubmit}>
              Save Changes
            </Button>
            <Button
              className="w-full"
              status="nobg"
              onClick={() => {
                setOpen(false);
                navigate("/log", { state: { selectedParticipant } });
              }}
            >
              Discard Changes
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const validateForm = async (event) => {
    event.preventDefault();

    const newDetails = new FormData(event.currentTarget);

    const formSchema = z.object({
      activity_location: z.string(), // Redundant
      activity_name: z.string(), // Redundant
      activity_time: z.number(), // Redundant
      activity_notes: z.string(),
    });

    try {
      const parsedData = {
        activity_location: newDetails.get("activity_location"), // Redundant
        activity_name: newDetails.get("activity_name"), // Redundant
        activity_time: parseInt(newDetails.get("activity_time")), // Redundant
        activity_notes: newDetails.get("activity_notes"),
      };

      formSchema.parse(parsedData);

      if(parsedData?.activity_location || parsedData?.activity_name || parsedData?.activity_time){
        await updateManualActivity(parsedData);
      } else {
        await updateActivityNotes(parsedData);
      }

      // Reset unsaved changes after successful submission
      setUnsavedChanges(false);
    } catch (err) {
      LogErrors(user, logout, updateTokens, err);
      if (err instanceof z.ZodError) {
        const errors = err.flatten().fieldErrors;
        LogErrors(user, logout, updateTokens, errors);
        // setErrors(() => errors);
      }
    }
  };

  const updateManualActivity = async (parsedData) => {
    await Sentry.startSpan({ name: "Update Manual Activity", op: "http.request" }, async (span) => {
      try {
        const response = await fetchWithRefresh(
          `${import.meta.env.VITE_API_URL}Activity/UpdateManualActivity`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user.token}`,
            },
            body: JSON.stringify({
              activityid: activity.activityId,
              activityName: parsedData.activity_name,
              activityLocation: parsedData.activity_location,
              notes: parsedData.activity_notes,
            }),
          },
          logout,
          updateTokens,
          (message) => {
            toast.info(message);
          }
        );
  
        span.setAttribute("http.status_code", response.status);
  
        if (response.ok) {
          navigate("/log", { state: { selectedParticipant } });
        } else {
          const errorData = await response.json();
          Sentry.captureMessage("Failed to update activity", { extra: errorData });
          LogErrors(user, logout, updateTokens, "Error Data: " + errorData);
          throw new Error("Failed to update activity");
        }
      } catch (error) {
        Sentry.captureException(error);
        LogErrors(user, logout, updateTokens, error);
        span.setStatus("error");
      } finally {
        span.end();
      }
    });
  };

  const updateActivityNotes = async (parsedData) => {
    await Sentry.startSpan({ name: "Update Activity Notes", op: "http.request" }, async (span) => {
      try {
        const response = await fetchWithRefresh(
          `${import.meta.env.VITE_API_URL}Activity/UpdateActivityNotes`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user.token}`,
            },
            body: JSON.stringify({
              activityid: activity.activityId,
              activitynotes: parsedData.activity_notes,
            }),
          },
          logout,
          updateTokens,
          (message) => {
            toast.info(message);
          }
        );
  
        span.setAttribute("http.status_code", response.status);
  
        if (response.ok) {
          navigate("/log", { state: { selectedParticipant } });
        } else {
          const errorData = await response.json();
          Sentry.captureMessage("Failed to update activity notes", { extra: errorData });
          LogErrors(user, logout, updateTokens, "Error Data: " + errorData);
          throw new Error("Failed to update activity notes");
        }
      } catch (error) {
        Sentry.captureException(error);
        LogErrors(user, logout, updateTokens, error);
        span.setStatus("error");
      } finally {
        span.end();
      }
    });
  };

  const deleteManualActivity = async () => {
    await Sentry.startSpan({ name: "Delete Manual Activity", op: "http.request" }, async (span) => {
      try {
        const response = await fetchWithRefresh(
          `${import.meta.env.VITE_API_URL}Activity/DeleteManualActivity?activityId=${activity.activityId}`,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user.token}`,
            },
          },
          logout,
          updateTokens,
          (message) => {
            toast.info(message);
          }
        );
  
        span.setAttribute("http.status_code", response.status);
  
        if (response.ok) {
          navigate("/log", { state: { selectedParticipant } });
        } else {
          const errorData = await response.json();
          Sentry.captureMessage("Failed to delete activity", { extra: errorData });
          LogErrors(user, logout, updateTokens, "Error Data: " + errorData);
          throw new Error("Failed to delete activity");
        }
      } catch (error) {
        Sentry.captureException(error);
        LogErrors(user, logout, updateTokens, error);
        span.setStatus("error");
      } finally {
        span.end();
      }
    });
  };

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    const remainingMinutes = minutes % 60;
    const remainingSeconds = seconds % 60;
  
    if (days > 0) {
      return `${days} ${days === 1 ? 'day' : 'days'}${remainingHours > 0 ? ` ${remainingHours} ${remainingHours === 1 ? 'hour' : 'hours'}` : ''}${remainingMinutes > 0 ? ` ${remainingMinutes} ${remainingMinutes === 1 ? 'minute' : 'minutes'}` : ''}${remainingSeconds > 0 ? ` ${remainingSeconds} ${remainingSeconds === 1 ? 'second' : 'seconds'}` : ''}`;
    } else if (hours > 0) {
      return `${hours} ${hours === 1 ? 'hour' : 'hours'}${remainingMinutes > 0 ? ` ${remainingMinutes} ${remainingMinutes === 1 ? 'minute' : 'minutes'}` : ''}${remainingSeconds > 0 ? ` ${remainingSeconds} ${remainingSeconds === 1 ? 'second' : 'seconds'}` : ''}`;
    } else if (minutes > 0) {
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}${remainingSeconds > 0 ? ` ${remainingSeconds} ${remainingSeconds === 1 ? 'second' : 'seconds'}` : ''}`;
    } else {
      return `${remainingSeconds} ${remainingSeconds === 1 ? 'second' : 'seconds'}`;
    }
  };

  return (
    <div>
      <form onSubmit={validateForm} className="space-y-4" ref={formRef}>
        <div className={`${(user.subscription.Name === 'Bronze' || activity?.editableStatus === "0") && "opacity-50 cursor-auto"}`}>
          <TextInput
            className={`cursor-auto capitalize`}
            type="text"
            label="Activity Location"
            name="activity_location"
            readOnly={user.subscription.Name === 'Bronze' || activity?.editableStatus === "0"}
            defaultValue={formDetails.activity_location}
            onChange={(e) => {
              inputChanged(e.target.name, e.target.value);
            }}
          />
        </div>
        <div className={`${(user.subscription.Name === 'Bronze' || activity?.editableStatus === "0") && "opacity-50 cursor-auto"}`}>
          <TextInput
            className={`cursor-auto capitalize`}
            type="text"
            label="Activity Name"
            name="activity_name"
            readOnly={user.subscription.Name === 'Bronze' || activity?.editableStatus === "0"}
            defaultValue={formDetails.activity_name}
            onChange={(e) => {
              inputChanged(e.target.name, e.target.value);
            }}
          />
        </div>
        <div className={`opacity-50 cursor-auto`}>
          <TextInput
            className={`cursor-auto capitalize`}
            type="text"
            label="Time Taken"
            name="activity_time"
            readOnly
            defaultValue={activity.duration === 0 ? '0' : formatDuration(activity.duration)}
            onChange={(e) => {
              inputChanged(e.target.name, e.target.value);
            }}
          />
        </div>
        <div className={`${user.subscription.Name === 'Bronze' ? 'opacity-50 cursor-auto' : ''}`}>
          <TextArea
            label="Notes"
            name="activity_notes"
            defaultValue={formDetails.activity_notes}
            readOnly={user.subscription.Name === 'Bronze'}
            onChange={(e) => {
              inputChanged(e.target.name, e.target.value);
            }}
          />
        </div>
        {inputChanged && user.subscription.Name !== 'Bronze' && (
          <Button submit={true} className="w-full">
            Update Activity
          </Button>
        )}
        {activity.editableStatus === '1' && user.subscription.Name !== 'Bronze' && (
          <Button className="w-full" onClick={() => deleteManualActivity()}>
            Delete
          </Button>
        )}
      </form>
    </div>
  );
}